import React, { useState } from 'react';
import { Link2, Copy, AlertTriangle, CheckCircle } from 'lucide-react';

const InternalLinkAnalyzerTool = () => {
  const [html, setHtml] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [analysis, setAnalysis] = useState(null);

  const analyzeLinks = () => {
    if (!html.trim()) {
      setAnalysis(null);
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = doc.querySelectorAll('a[href]');
    
    const baseHost = baseUrl ? new URL(baseUrl).hostname : null;

    const linkData = Array.from(links).map(link => {
      const href = link.getAttribute('href');
      const text = link.textContent.trim();
      const rel = link.getAttribute('rel') || '';
      const target = link.getAttribute('target') || '';
      const title = link.getAttribute('title') || '';

      let type = 'unknown';
      let isInternal = false;

      try {
        if (href.startsWith('#')) {
          type = 'anchor';
          isInternal = true;
        } else if (href.startsWith('mailto:')) {
          type = 'email';
        } else if (href.startsWith('tel:')) {
          type = 'phone';
        } else if (href.startsWith('/') || href.startsWith('./')) {
          type = 'relative';
          isInternal = true;
        } else if (href.startsWith('http')) {
          const linkHost = new URL(href).hostname;
          isInternal = baseHost && linkHost === baseHost;
          type = isInternal ? 'absolute-internal' : 'external';
        }
      } catch {
        type = 'invalid';
      }

      return { href, text, rel, target, title, type, isInternal };
    });

    const internal = linkData.filter(l => l.isInternal);
    const external = linkData.filter(l => !l.isInternal && l.type !== 'email' && l.type !== 'phone' && l.type !== 'anchor');
    const anchors = linkData.filter(l => l.type === 'anchor');

    const issues = [];
    const suggestions = [];

    // Check for issues
    linkData.forEach(link => {
      if (!link.text && link.type !== 'anchor') {
        issues.push({ type: 'error', text: `Empty anchor text for: ${link.href}` });
      }
      if (link.text && link.text.toLowerCase() === 'click here') {
        issues.push({ type: 'warning', text: `Generic anchor text "click here" for: ${link.href}` });
      }
      if (link.isInternal && link.rel.includes('nofollow')) {
        issues.push({ type: 'warning', text: `Internal link with nofollow: ${link.href}` });
      }
      if (!link.isInternal && !link.rel.includes('noopener') && link.target === '_blank') {
        issues.push({ type: 'warning', text: `External link without rel="noopener": ${link.href}` });
      }
    });

    // Generate suggestions
    if (internal.length < 3) {
      suggestions.push('Add more internal links to improve site navigation and SEO');
    }
    if (external.length > internal.length * 2) {
      suggestions.push('Consider adding more internal links - external links outnumber internal links significantly');
    }
    const uniqueInternalUrls = new Set(internal.map(l => l.href));
    if (uniqueInternalUrls.size < internal.length * 0.5) {
      suggestions.push('Diversify your internal links - many point to the same pages');
    }

    setAnalysis({
      total: linkData.length,
      internal,
      external,
      anchors,
      issues,
      suggestions,
      linkData
    });
  };

  const loadSample = () => {
    setBaseUrl('https://example.com');
    setHtml(`<article>
  <h1>SEO Best Practices</h1>
  <p>Learn about <a href="/seo-guide">our complete SEO guide</a> for beginners.</p>
  <p>Check out <a href="https://example.com/tools">our tools</a> section.</p>
  <p>Read more on <a href="https://google.com" target="_blank">Google's guidelines</a>.</p>
  <p>For questions, <a href="mailto:info@example.com">email us</a>.</p>
  <p>Jump to <a href="#conclusion">conclusion</a>.</p>
  <p><a href="/contact">Click here</a> to contact us.</p>
  <p>Visit <a href="https://moz.com" target="_blank" rel="noopener">Moz</a> for more resources.</p>
  <h2 id="conclusion">Conclusion</h2>
</article>`);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Base URL (for determining internal links)</label>
        <input
          type="url"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://yoursite.com"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-300">HTML Content</label>
          <button onClick={loadSample} className="text-xs text-blue-400 hover:text-blue-300">
            Load Sample
          </button>
        </div>
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          placeholder="Paste your HTML content..."
          className="w-full h-40 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      <button
        onClick={analyzeLinks}
        disabled={!html.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <Link2 size={18} />
        Analyze Links
      </button>

      {analysis && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-2xl font-bold text-white">{analysis.total}</div>
              <div className="text-xs text-gray-500">Total Links</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-400">{analysis.internal.length}</div>
              <div className="text-xs text-gray-500">Internal</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">{analysis.external.length}</div>
              <div className="text-xs text-gray-500">External</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-400">{analysis.anchors.length}</div>
              <div className="text-xs text-gray-500">Anchors</div>
            </div>
          </div>

          {/* Issues */}
          {analysis.issues.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-300">Issues ({analysis.issues.length})</h3>
              {analysis.issues.map((issue, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 p-3 rounded-lg ${
                    issue.type === 'error' ? 'bg-red-900/30 text-red-400' : 'bg-yellow-900/30 text-yellow-400'
                  }`}
                >
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{issue.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Link List */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-300">All Links</h3>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {analysis.linkData.map((link, i) => (
                <div key={i} className="p-3 bg-gray-800 rounded-lg text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      link.isInternal ? 'bg-green-600' : 
                      link.type === 'external' ? 'bg-blue-600' :
                      link.type === 'anchor' ? 'bg-purple-600' :
                      'bg-gray-600'
                    }`}>
                      {link.type}
                    </span>
                    <span className="text-gray-400 truncate">{link.href}</span>
                  </div>
                  {link.text && <div className="text-white">"{link.text}"</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          {analysis.suggestions.length > 0 && (
            <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
              <h3 className="text-sm font-medium text-blue-300 mb-2">Suggestions</h3>
              <ul className="space-y-1 text-sm text-gray-400">
                {analysis.suggestions.map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InternalLinkAnalyzerTool;
